/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 *
 * ===================== CHANGELOG (bugfix pass) =====================
 * Search for "NEW (bugfix)" comments below to find the exact changed
 * lines. Everything else in this file is unchanged.
 *
 * 1. updateSizeCards(): size cards now compute is-nonexistent /
 *    is-unavailable classes on their own input directly from variant
 *    data, instead of relying only on updateAvailability() (which is
 *    gated by the block's "Show availability in selectors" setting
 *    and can differ per store/template — this is why the disabled/
 *    slash styling worked on the US product but not the Canadian
 *    one). This is purely additive: if updateAvailability() also
 *    runs, both read the same variant data and agree.
 * 2. updateSizeCards(): also sets the native `disabled` property on
 *    the radio input itself (not just CSS pointer-events) whenever a
 *    size is nonexistent or genuinely out of stock (not backorder-
 *    allowed), so it's fully unreachable via keyboard/Tab too, not
 *    just unclickable with a mouse. Backorder-allowed sizes (Shopify
 *    "continue selling when out of stock") stay selectable, since
 *    variant.available already reports true for those.
 * 3. updateSizeCards(): when a size doesn't exist at all for the
 *    selected color, the card now shows only a translated "no stock"
 *    message (theme.strings.noStock — the same string already
 *    maintained for the Inventory Status block) instead of borrowed
 *    spec rows from a different color's variant. This only applies
 *    to genuinely nonexistent combos; a combo that exists but is
 *    simply sold out still shows its real spec rows, unchanged.
 * =====================================================================
 */

if (!customElements.get("variant-picker")) {
  function updateBadgeDisplay(variantId, sectionEl) {
    if (!sectionEl) return;

    const badgeRow = sectionEl.querySelector(".badge-display-row");
    const badgeSpan = sectionEl.querySelector(".variant-badge-value");
    const longLeadNote = sectionEl.querySelector(".long-lead-note");
    if (!badgeRow || !badgeSpan) return;

    // If no valid variantId, hide the badge and return early
    if (!variantId) {
      badgeSpan.textContent = "";
      badgeRow.style.display = "none";
      if (longLeadNote) longLeadNote.style.display = "none";
      return;
    }

    const key = String(variantId);
    const variantData = window.variantMetafieldData?.[key];
    if (!variantData) {
      badgeSpan.textContent = "";
      badgeRow.style.display = "none";
      if (longLeadNote) longLeadNote.style.display = "none";
      return;
    }

    // Read badge value from possible fields (badge_value or badge)
    const badgeValueRaw = variantData.badge_value ?? variantData.badge ?? "";
    const badgeValue = String(badgeValueRaw).trim();
    if (!badgeValue) {
      badgeSpan.textContent = "";
      badgeRow.style.display = "none";
      if (longLeadNote) longLeadNote.style.display = "none";
      return;
    }

    // Reset classes
    badgeSpan.classList.remove(
      "badge-green",
      "badge-yellow",
      "badge-red",
      "badge-blue",
    );

    // Determine icon / class by normalized value (case-insensitive)
    let icon = "";
    const v = badgeValue.toLowerCase();

    if (v === "in stock") {
      badgeSpan.classList.add("badge-green");
      icon = "✓";
      if (longLeadNote) longLeadNote.style.display = "none";
    } else if (v === "long lead time") {
      badgeSpan.classList.add("badge-yellow");
      icon = "⏳";
      if (longLeadNote) longLeadNote.style.display = "block";
    } else if (v === "out of stock") {
      badgeSpan.classList.add("badge-red");
      icon = "✖";
      if (longLeadNote) longLeadNote.style.display = "none";
    } else if (v === "discontinued") {
      badgeSpan.classList.add("badge-blue");
      icon = "⚠️";
      if (longLeadNote) longLeadNote.style.display = "none";
    } else {
      if (longLeadNote) longLeadNote.style.display = "none";
      // 🚫 Unknown badge → hide it
      badgeSpan.textContent = "";
      badgeRow.style.display = "none";
      return;
    }

    // Render the badge
    badgeSpan.textContent = icon ? `${icon} ${badgeValue}` : badgeValue;
    badgeRow.style.display = ""; // let CSS determine display (or use 'block' if you prefer)
    badgeRow.style.margin = "0";
    badgeRow.style.padding = "0";
  }

  // This function will handle displaying your UOM metafield
  function updateMetafieldDisplay(variantId) {
    // Try new structure first (main-product-2.liquid)
    const uomBox = document.getElementById("variant-uom-box");
    const uomDisplayRow = document.getElementById("uom-display-row");

    // Fallback to old structure (main-product.liquid)
    const uomDisplayOld = document.getElementById("variant-uom-value");

    // Check if we have either structure
    const hasNewStructure = uomBox && uomDisplayRow;
    const hasOldStructure = uomDisplayOld && uomDisplayRow;

    if (!hasNewStructure && !hasOldStructure) {
      console.warn("updateMetafieldDisplay: UOM elements not found.");
      return;
    }

    if (!variantId) {
      // Clear both structures
      if (hasNewStructure) {
        const uomValueEl = uomBox.querySelector(".uom-value");
        const pricePerUnitEl = uomBox.querySelector(".price-per-unit");
        if (uomValueEl) uomValueEl.textContent = "";
        if (pricePerUnitEl) pricePerUnitEl.textContent = "";
      }
      if (hasOldStructure) {
        uomDisplayOld.textContent = "";
      }
      if (uomDisplayRow) uomDisplayRow.style.display = "none";
      return;
    }

    const key = String(variantId);
    const variantData = window.variantMetafieldData?.[key];

    if (!variantData) {
      // Clear both structures
      if (hasNewStructure) {
        const uomValueEl = uomBox.querySelector(".uom-value");
        const pricePerUnitEl = uomBox.querySelector(".price-per-unit");
        if (uomValueEl) uomValueEl.textContent = "";
        if (pricePerUnitEl) pricePerUnitEl.textContent = "";
      }
      if (hasOldStructure) {
        uomDisplayOld.textContent = "";
      }
      if (uomDisplayRow) uomDisplayRow.style.display = "none";
      return;
    }

    const uom = variantData.uom_value ?? variantData.uom ?? "";
    const uomNum = Number(String(uom).trim());

    if (uomNum && !isNaN(uomNum) && uomNum > 1) {
      // Calculate price per unit
      const priceCents = Number(variantData.price_cents);
      const perUnitCents = Math.round(priceCents / uomNum);
      const formatted = (perUnitCents / 100).toFixed(2);

      // Update NEW structure (main-product-2.liquid) with "-pack" suffix
      if (hasNewStructure) {
        const uomValueEl = uomBox.querySelector(".uom-value");
        const pricePerUnitEl = uomBox.querySelector(".price-per-unit");

        if (uomValueEl) {
          uomValueEl.textContent = String(uomNum) + "-pack";
        }
        if (pricePerUnitEl) {
          pricePerUnitEl.textContent = "$" + formatted + " per unit";
        }
      }

      // Update OLD structure (main-product.liquid) without "-pack"
      if (hasOldStructure) {
        uomDisplayOld.textContent = String(uomNum);
      }

      if (uomDisplayRow) uomDisplayRow.style.display = "";
    } else {
      // Clear both structures
      if (hasNewStructure) {
        const uomValueEl = uomBox.querySelector(".uom-value");
        const pricePerUnitEl = uomBox.querySelector(".price-per-unit");
        if (uomValueEl) uomValueEl.textContent = "";
        if (pricePerUnitEl) pricePerUnitEl.textContent = "";
      }
      if (hasOldStructure) {
        uomDisplayOld.textContent = "";
      }
      if (uomDisplayRow) uomDisplayRow.style.display = "none";
    }
  }

  // -----------------------
  // Delivery Window Logic
  // -----------------------
  function addBusinessDays(date, days) {
    const result = new Date(date);
    if (!Number.isInteger(days) || days <= 0) return result;
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const d = result.getDay();
      if (d !== 0 && d !== 6) added++;
    }
    return result;
  }

  function formatStart(d) {
    if (!d) return "";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  function formatEnd(d) {
    if (!d) return "";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }

  function normalizeBadge(v) {
    if (!v && v !== 0) return null;
    return String(v).trim().toLowerCase();
  }

  function deliveryRangeFromBadge(badgeValue) {
    const b = normalizeBadge(badgeValue);
    if (!b) return null;

    if (b === "in stock") return { min: 2, max: 5 };
    if (b === "long lead time") return { min: 10, max: 14 };

    if (b === "out of stock" || b === "discontinued") return null;

    return null;
  }

  function renderDelivery(deliveryEl, variantId) {
    if (!deliveryEl) return;

    if (!variantId) {
      deliveryEl.style.display = "none";
      return;
    }

    const wrapper =
      deliveryEl.closest(".product-delivery-window-box") || deliveryEl;
    const labelEl = deliveryEl.querySelector(".delivery-window-label");
    const valueEl = deliveryEl.querySelector(".delivery-window-value");

    if (!valueEl) return;

    const hide = () => {
      if (labelEl) labelEl.style.display = "none";
      if (valueEl) {
        valueEl.textContent = "";
        valueEl.style.display = "none";
      }
      try {
        wrapper.setAttribute("hidden", "");
      } catch (e) {}
      wrapper.style.display = "none";
      wrapper.classList.remove("has-content");
    };

    const show = () => {
      try {
        wrapper.removeAttribute("hidden");
      } catch (e) {}
      wrapper.classList.add("has-content");
      if (labelEl) labelEl.style.display = "inline";
      if (valueEl) valueEl.style.display = "inline";
    };

    const key = String(variantId);
    const meta =
      window.variantMetafieldData && window.variantMetafieldData[key];
    const badgeRaw =
      meta && (meta.badge_value ?? meta.badge)
        ? (meta.badge_value ?? meta.badge)
        : null;
    const badge = normalizeBadge(badgeRaw);

    if (!badge) {
      hide();
      return;
    }

    const range = deliveryRangeFromBadge(badge);

    if (!range) {
      // For Out of Stock / Discontinued / unknown badge -> hide completely
      hide();
      return;
    }

    const now = new Date();
    const startDate = addBusinessDays(now, range.min);
    const endDate = addBusinessDays(now, range.max);

    const startTxt = formatStart(startDate);
    const endTxt = formatEnd(endDate);

    if (range.min === range.max) {
      valueEl.textContent = `${startTxt}`;
    } else {
      valueEl.textContent = `${startTxt} — ${endTxt}`;
    }

    // Add data attribute based on badge type for CSS styling
    if (badge === "in stock") {
      valueEl.setAttribute("data-badge-type", "in-stock");
    } else if (badge === "long lead time") {
      valueEl.setAttribute("data-badge-type", "long-lead");
    } else {
      valueEl.removeAttribute("data-badge-type");
    }

    if (valueEl.textContent.trim() !== "") {
      show();
    } else {
      hide();
    }
  }
  // --------------------------
  // Delivery Window Logic End

  // Formats Shopify price (in cents) to a currency string
  function formatShopifyMoney(cents) {
    if (!cents || isNaN(cents)) return "";
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || "${{amount}}",
      );
    }
    return "$" + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // Size values treated as "the recommended/wide-style size" for the
  // default badge fallback ONLY (i.e. when a variant has no explicit
  // custom.size_badge metafield set). Language-agnostic: matches the
  // SIZE VALUE text itself (Wide/Ancho/Large/Ancha/...), not the
  // "Size" option NAME, so it works regardless of storefront locale.
  // Add more translated size-value words here if new languages/values
  // are introduced (e.g. "breit" for German, "大" for another locale).
  const WIDE_TERMS = ["wide", "ancho", "large", "ancha"];

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section?.querySelector(".js-product-form-main");
      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.deliveryEl = this.section?.querySelector('[id^="delivery-window-"]');
    }

    connectedCallback() {
      // Wait a bit for form and buttons to be rendered
      setTimeout(() => {
        if (this.variant && this.variant.id) {
          try {
            updateMetafieldDisplay(this.variant.id);
            updateBadgeDisplay(this.variant.id, this.section);
            this.updateButtonsForVariant(this.variant.id);

            if (this.deliveryEl)
              renderDelivery(this.deliveryEl, this.variant.id);
          } catch (err) {
            console.error("Initial update failed:", err);
          }
        }

        this.updateAvailability?.();
        this.updateAddToCartButton?.();
        this.updateSizeCards?.();
      }, 300); // 300ms delay ensures hydration done

      this.addEventListener("change", this.handleVariantChange.bind(this));
    }

    updateButtonsForVariant(variantId) {
      if (!variantId) return;

      const data = window.variantMetafieldData?.[variantId];
      if (!data) return;

      const badge =
        data.badge_value ||
        data.badge ||
        data.Badge ||
        data.badge_label ||
        null;
      const shouldDisable =
        badge && ["out of stock", "discontinued"].includes(badge.toLowerCase());

      if (!this.productForm)
        this.productForm = this.section?.querySelector(".js-product-form-main");

      const addBtn = this.productForm?.querySelector(
        '[name="add"], .product-form__submit, [data-add-to-cart], .product_addBtn',
      );
      const buyBtn = this.productForm?.querySelector(
        '#buy-now, .buy-now__button, .shopify-payment-button__button, [data-shopify="payment-button"]',
      );

      const setBtnDisabled = (btn, state, text = "Unavailable") => {
        if (!btn) return;
        btn.disabled = state;
        btn.style.opacity = state ? "0.5" : "1";
        btn.style.pointerEvents = state ? "none" : "auto";
        if (state) {
          if (!btn.dataset.originalText)
            btn.dataset.originalText = btn.textContent.trim();
          btn.textContent = text;
        } else if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
          delete btn.dataset.originalText;
        }
      };

      setBtnDisabled(addBtn, shouldDisable, "Sold Out");
      setBtnDisabled(buyBtn, shouldDisable);
    }

    handleVariantChange(evt) {
      try {
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        this.preSelection =
          !this.variant &&
          this.selectedOptions.find((o) => o === null) === null;

        if (this.variant) {
          this.updateMedia?.();
          this.updateUrl?.(evt);
          this.updateVariantInput?.();

          updateMetafieldDisplay(this.variant.id);
          updateBadgeDisplay(this.variant.id, this.section);
          this.updateButtonsForVariant(this.variant.id);

          if (this.deliveryEl) renderDelivery(this.deliveryEl, this.variant.id);
        } else {
          updateMetafieldDisplay(null);
          updateBadgeDisplay(null, this.section);
          if (this.deliveryEl) renderDelivery(this.deliveryEl, null);
        }

        this.updateAddToCartButton?.();
        this.updateAvailability?.();
        this.updatePrice?.();
        this.updateWeight?.();
        this.updateBarcode?.();
        this.updateBackorderText?.();
        this.updatePickupAvailability?.();
        this.updateSku?.();
        this.updateMetafieldVisibility?.();
        this.updateSizeCards?.();
        VariantPicker.updateLabelText?.(evt);

        if (!this.preSelection) {
          this.dispatchEvent(
            new CustomEvent("on:variant:change", {
              bubbles: true,
              detail: {
                form: this.productForm,
                variant: this.variant,
                product: this.data.product,
              },
            }),
          );
        }
      } catch (error) {
        console.error("Error in handleVariantChange:", error);
        this.updateAddToCartButton?.();
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll("[data-variant-metafield]").forEach((elem) => {
        elem.classList.toggle(
          "hidden",
          elem.dataset.variantMetafield !== this.variant?.id.toString(),
        );
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      try {
        this.productForm = this.section.querySelector(".js-product-form-main");
        if (!this.productForm) return;

        this.addBtn =
          this.addBtn || this.productForm.querySelector('[name="add"]');
        this.buyNowBtn =
          this.buyNowBtn || this.section.querySelector("#buy-now");
        if (!this.addBtn && !this.buyNowBtn) return;

        const variantId = this.variant?.id;
        const variantAvailable = this.variant?.available;
        const noVariant = !variantId;

        const labelOut = theme.strings?.noStock || "Out of stock";
        const labelUnavailable = theme.strings?.noVariant || "Unavailable";
        const labelAdd = this.addBtn?.dataset.addToCartText || "Add to cart";
        const labelBuy = this.buyNowBtn?.dataset.buyNowText || "Buy now";

        if (this.addBtn) {
          if (noVariant) {
            this.addBtn.disabled = true;
            this.addBtn.textContent = labelAdd;
          } else {
            this.addBtn.disabled = !variantAvailable;
            this.addBtn.textContent = variantAvailable ? labelAdd : labelOut;
          }
        }

        if (this.buyNowBtn) {
          if (noVariant) {
            this.buyNowBtn.disabled = true;
            this.buyNowBtn.textContent = labelUnavailable;
          } else {
            this.buyNowBtn.disabled = !variantAvailable;
            this.buyNowBtn.textContent = variantAvailable ? labelBuy : labelOut;
          }
        }
      } catch (error) {
        console.error("Error in updateAddToCartButton:", error);
        if (this.addBtn) {
          this.addBtn.disabled = false;
          this.addBtn.textContent = "Add to cart";
        }
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = false;
          this.buyNowBtn.textContent = "Buy now";
        }
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      try {
        const el = optionEl;
        const unavailableText = exists
          ? theme.strings?.noStock || "Out of stock"
          : theme.strings?.noVariant || "Unavailable";
        el.classList.toggle("is-unavailable", !available);
        el.classList.toggle("is-nonexistent", !exists);

        if (optionEl.classList.contains("custom-select__option")) {
          const em = el.querySelector("em");

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = unavailableText;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
            }
          }
        } else if (!available) {
          if (el.nextElementSibling) {
            el.nextElementSibling.title = unavailableText;
          }
        } else {
          if (el.nextElementSibling) {
            el.nextElementSibling.removeAttribute("title");
          }
        }
      } catch (error) {
        console.error("Error in updateOptionAvailability:", error);
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      try {
        if (this.dataset.showAvailability === "false") return;
        const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
        let currVariant = this.variant;

        if (!this.variant) {
          currVariant = { options: this.selectedOptions };
        }

        if (availabilityMethod === "selection") {
          // Flag all options as unavailable
          this.querySelectorAll(".js-option").forEach((optionEl) => {
            VariantPicker.updateOptionAvailability(optionEl, false, false);
          });

          // Flag selector options as available or sold out, depending on the variant availability
          this.optionSelectors.forEach((selector, selectorIndex) => {
            this.data.product.variants.forEach((variant) => {
              let matchCount = 0;

              variant.options.forEach((option, optionIndex) => {
                if (
                  option === currVariant.options[optionIndex] &&
                  optionIndex !== selectorIndex
                ) {
                  matchCount += 1;
                }
              });

              if (matchCount === currVariant.options.length - 1) {
                const options = selector.querySelectorAll(".js-option");
                const optionEl = Array.from(options).find((opt) => {
                  if (selector.dataset.selectorType === "dropdown") {
                    return opt.dataset.value === variant.options[selectorIndex];
                  }
                  return opt.value === variant.options[selectorIndex];
                });

                if (optionEl) {
                  VariantPicker.updateOptionAvailability(
                    optionEl,
                    true,
                    variant.available,
                  );
                }
              }
            });
          });
        } else {
          this.optionSelectors.forEach((selector, selectorIndex) => {
            const options = selector.querySelectorAll(
              '.js-option:not([data-value=""])',
            );
            options.forEach((option) => {
              const optionValue =
                selector.dataset.selectorType === "dropdown"
                  ? option.dataset.value
                  : option.value;
              // any available variants with previous options and this one locked in?
              let variantsExist = false;
              let variantsAvailable = false;
              this.data.product.variants.forEach((v) => {
                let matches = 0;
                for (let i = 0; i < selectorIndex; i += 1) {
                  if (
                    v.options[i] === this.selectedOptions[i] ||
                    this.selectedOptions[i] === null
                  ) {
                    matches += 1;
                  }
                }
                if (
                  v.options[selectorIndex] === optionValue &&
                  matches === selectorIndex
                ) {
                  variantsExist = true;
                  if (v.available) {
                    variantsAvailable = true;
                  }
                }
              });
              VariantPicker.updateOptionAvailability(
                option,
                variantsExist,
                variantsAvailable,
              );
            });
          });
        }
      } catch (error) {
        console.error("Error in updateAvailability:", error);
      }
    }

    /**
     * Updates size card spec rows and badge to match the currently
     * selected color (or overall selection), driven by variantExtra
     * data keyed by variant id.
     *
     * Badge priority:
     *   1. Per-variant custom.size_badge metafield (variantExtra.badge)
     *   2. block.settings.size_badge_text (defaultBadgeText) — ONLY for
     *      size values matching WIDE_TERMS, checked against the size
     *      VALUE text (Wide/Ancho/Large/...), so it works in any
     *      storefront language without extra config.
     *   3. No badge shown
     *
     * IMPORTANT — cards never show a "Sold out"/"Unavailable" text
     * label inside the body. Unavailability is communicated purely via
     * the disabled/dimmed card styling and slashed radio ring (CSS).
     * The badge (BEST FIT, In Stock, etc.) always stays visible even
     * when the combo is sold out or doesn't exist for this
     * color — the CSS blurs it slightly in that case, but it is never
     * removed. Likewise, spec rows are never blanked: if the exact
     * color+size combo doesn't exist, we fall back to any variant that
     * matches this size so the card still shows representative specs.
     */
    updateSizeCards() {
      try {
        const sizeSelector = this.querySelector('[data-size-selector="true"]');
        if (!sizeSelector) return;

        const colorSelector = this.querySelector('[data-color-selector="true"]');
        const checkedColor = colorSelector
          ? colorSelector.querySelector('input:checked')
          : null;
        const colorValue = checkedColor ? checkedColor.value : null;

        const variantExtra = (this.data && this.data.variantExtra) || {};
        const defaultBadgeText = (this.data && this.data.defaultBadgeText) || "";

        sizeSelector.querySelectorAll('.opt-label--size-card').forEach((card) => {
          const sizeValue = card.dataset.value;
          const input = document.getElementById(card.getAttribute('for'));
          if (!input) return;

          const variant = this.data.product.variants.find((v) => {
            const matchesSize = v.options.includes(sizeValue);
            const matchesColor = colorValue ? v.options.includes(colorValue) : true;
            return matchesSize && matchesColor;
          });

          // ===================== NEW (bugfix) =========================
          // Previously, the disabled/slashed look on size cards relied
          // entirely on updateAvailability(), which only runs its class
          // toggling when data-show-availability !== "false" (driven by
          // the block setting "Show availability in selectors", which
          // can differ per store/template — this is why the same cards
          // worked on the US product but not on the Canadian one).
          //
          // Size cards now compute their own existence/availability
          // directly from variant data, independent of that setting or
          // of "downward" vs "selection" availability mode. This runs
          // every time updateSizeCards() runs (on load + every
          // color/size change), so the slash + dimmed state is always
          // consistent on every store/language/template, with or
          // without updateAvailability() also running. If both run,
          // they agree because they read the same variant data — no
          // conflict, nothing else about updateAvailability() changed.
          const exists = !!variant;
          const available = exists && variant.available;
          input.classList.toggle('is-nonexistent', !exists);
          input.classList.toggle('is-unavailable', !available);

          // ===================== NEW (hardening) =======================
          // Previously only pointer-events:none (CSS) blocked disabled
          // cards from being clicked with a mouse, but the radio input
          // itself was never marked disabled — so it remained reachable
          // and selectable via keyboard/Tab + Space, even though the
          // card visually looked inert. Setting the native `disabled`
          // property closes that gap: browsers skip disabled inputs in
          // tab order and block all interaction with them, matching the
          // visual state exactly. Note: `available` already accounts
          // for Shopify's "continue selling when out of stock" setting
          // (backorder-allowed variants report available === true), so
          // a backordered-but-purchasable size stays fully selectable —
          // only truly nonexistent or genuinely out-of-stock (not
          // backorderable) combos get locked out here.
          input.disabled = !exists || !available;
          // =================== END NEW (hardening) ======================
          // =================== END NEW (bugfix) =========================

          const displayVariant =
            variant ||
            this.data.product.variants.find((v) => v.options.includes(sizeValue));

          const detailsEl = card.querySelector('.size-card-details');
          let badgeEl = card.querySelector('.size-card-badge');

          // ── Badge: resolved independently of stock/existence, and
          // always kept visible (CSS handles blurring when disabled). ──
          const extra = displayVariant
            ? (variantExtra[String(displayVariant.id)] || {})
            : {};
          let badgeText = (extra.badge || '').toString().trim();
          if (!badgeText && defaultBadgeText) {
            const sizeLower = String(sizeValue || '').toLowerCase();
            const isWideLike = WIDE_TERMS.some((term) => sizeLower.includes(term));
            if (isWideLike) badgeText = defaultBadgeText;
          }

          if (badgeText) {
            if (!badgeEl) {
              badgeEl = document.createElement('span');
              badgeEl.className = 'size-card-badge';
              card.prepend(badgeEl);
            }
            badgeEl.textContent = badgeText;
          } else if (badgeEl) {
            badgeEl.remove();
          }

          // ===================== NEW (bugfix) =========================
          // Previously, when a size didn't exist for the selected color
          // (exists === false), the card still showed spec rows borrowed
          // from a same-size variant of a DIFFERENT color — misleading,
          // since those specs don't actually apply to this color/size
          // combo at all. Now: if the combo genuinely doesn't exist,
          // the card shows ONLY a translated "no stock" message instead
          // of any spec rows — reusing theme.strings.noStock, the same
          // translation string already maintained for the Inventory
          // Status block (Translate & Adapt → Inventory Status → "Text
          // when out of stock"), so no new hardcoded text or separate
          // translation upkeep is needed for French/Spanish/etc.
          //
          // This ONLY applies when the combo doesn't exist at all. When
          // the combo DOES exist but is simply sold out (exists === true,
          // available === false), the real spec rows for that exact
          // variant still show as before — unchanged from prior behavior.
          if (detailsEl) {
            if (!exists) {
              const noStockText =
                (typeof theme !== 'undefined' && theme.strings && theme.strings.noStock) ||
                'Sold out';
              detailsEl.innerHTML =
                `<div class="spec-row"><span class="spec-value">${noStockText}</span></div>`;
            } else {
              const specs = (variantExtra[String(variant.id)] || {}).specs;
              if (specs && Object.keys(specs).length) {
                detailsEl.innerHTML = Object.entries(specs)
                  .map(
                    ([k, v]) =>
                      `<div class="spec-row"><span class="spec-label">${k}</span><span class="spec-value">${v}</span></div>`,
                  )
                  .join('');
              } else {
                detailsEl.innerHTML =
                  '<div class="spec-row"><span class="spec-value"></span></div>';
              }
            }
          }
          // =================== END NEW (bugfix) =========================
        });
      } catch (error) {
        console.error('Error in updateSizeCards:', error);
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product",
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery =
          this.mediaGallery || this.section.querySelector("media-gallery");
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`,
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      try {
        // ensure we capture the price container first
        this.price =
          this.price ||
          this.section.querySelector(".product-info__price > .price");
        if (!this.price) {
          console.warn(
            "updatePrice: outer price container not found in this.section",
          );
          return;
        }

        let { variant } = this;
        if (this.preSelection) {
          variant = this.data.product.variants[0];
          for (let i = 1; i < this.data.product.variants.length; i += 1) {
            if (this.data.product.variants[i].price < variant.price)
              variant = this.data.product.variants[i];
          }
        }

        if (!variant) {
          console.warn("updatePrice: no variant available");
          return;
        }

        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        if (!priceCurrentEl) {
          console.warn(
            "updatePrice: .price__current NOT found inside price container",
            this.price,
          );
          return; // avoid doing anything further if main container is missing
        }

        // safe helpers
        const q = (el, sel) => (el ? el.querySelector(sel) : null);
        const safeSetText = (el, text) => {
          if (el) el.textContent = text;
        };
        const safeSetHTML = (el, html) => {
          if (el) el.innerHTML = html;
        };

        // formatted lookup using string key
        const formatted =
          (this.data.formatted && this.data.formatted[String(variant.id)]) ||
          {};

        // get nested elements (may be null if template differs)
        const currentLabelEl = q(priceCurrentEl, ".js-label");
        const currentValueEl = q(priceCurrentEl, ".js-value");

        const wasLabelEl = q(priceWasEl, ".js-label");
        const wasValueEl = q(priceWasEl, ".js-value");

        if (variant.compare_at_price > variant.price) {
          // sale
          if (currentLabelEl)
            safeSetText(currentLabelEl, theme.strings?.salePrice ?? "");
          if (currentValueEl)
            safeSetHTML(currentValueEl, formatted.price || "");
          else
            priceCurrentEl.innerHTML = `${theme.strings?.salePrice ?? ""} ${formatted.price || ""}`;

          if (priceWasEl) {
            if (wasLabelEl)
              safeSetText(wasLabelEl, theme.strings?.regularPrice ?? "");
            if (wasValueEl)
              safeSetHTML(wasValueEl, formatted.compareAtPrice || "");
            else priceWasEl.innerHTML = formatted.compareAtPrice || "";
          }
        } else {
          if (currentLabelEl)
            safeSetText(currentLabelEl, theme.strings?.regularPrice ?? "");
          if (currentValueEl)
            safeSetHTML(currentValueEl, formatted.price || "");
          else
            priceCurrentEl.innerHTML = `${theme.strings?.regularPrice ?? ""} ${formatted.price || ""}`;

          if (priceWasEl) {
            if (wasLabelEl) safeSetText(wasLabelEl, "");
            if (wasValueEl) safeSetHTML(wasValueEl, "");
            else priceWasEl.innerHTML = "";
          }
        }

        // unit price handling
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          if (valueEl)
            safeSetHTML(valueEl, formatted.unitPrice || valueEl.innerHTML);
          if (unitEl)
            unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          if (unitPriceEl) unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        // toggle classes & fallback blocks safely
        this.price.classList.toggle(
          "price--on-sale",
          variant.compare_at_price > variant.price,
        );
        this.price.classList.toggle(
          "price--sold-out",
          !variant.available && !this.preSelection,
        );

        // ── Savings block update ──────────────────────────────────────────────
        const priceDefault = this.price.querySelector(".price__default");
        if (priceDefault) {
          // Always remove existing savings so we rebuild fresh for this variant
          const existingSavings = priceDefault.querySelector(".js-savings");
          if (existingSavings) existingSavings.remove();

          const compareAt = variant.compare_at_price || 0;
          const currentPrice = variant.price || 0;

          if (compareAt > currentPrice) {
            const savings = compareAt - currentPrice;
            const savingsEl = document.createElement("div");
            savingsEl.classList.add("price__discount", "js-savings");

            if (compareAt > 100) {
              savingsEl.innerHTML = `You save&nbsp;${formatShopifyMoney(savings)}`;
            } else {
              const percentOff = Math.round((savings / compareAt) * 100);
              savingsEl.textContent = `${percentOff}% off`;
            }

            const perUnit = priceDefault.querySelector(".product-per-unit");
            if (perUnit) {
              priceDefault.insertBefore(savingsEl, perUnit);
            } else {
              priceDefault.appendChild(savingsEl);
            }
          }
        }
        // ── End savings block ─────────────────────────────────────────────────

        const defaultBlock = this.price.querySelector(".price__default");
        if (defaultBlock) defaultBlock.hidden = !variant && !this.preSelection;

        const noVariantBlock = this.price.querySelector(".price__no-variant");
        if (noVariantBlock)
          noVariantBlock.hidden = variant || this.preSelection;

        const from = this.price.querySelector(".price__from");
        if (from) from.hidden = !this.preSelection;
      } catch (error) {
        console.error("Error in updatePrice:", error);
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights =
        this.weights || this.section.querySelectorAll(".product-info__weight");
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id].weight
          : "";
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes =
        this.barcodes ||
        this.section.querySelectorAll(".product-info__barcode-value");
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.variant.id}`,
      );
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(
          ".js-product-form-main, .js-instalments-form",
        );

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selectedText = selector
            .querySelector(".custom-select__btn")
            .textContent.trim();
          selectedOptions.push(
            selectedText === this.dataset.placeholderText ? null : selectedText,
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      try {
        const selectedOptions = this.getSelectedOptions();
        return (
          this.data.product.variants.find((v) =>
            v.options.every((val, index) => val === selectedOptions[index]),
          ) || null
        );
      } catch (error) {
        console.error("Error in getSelectedVariant:", error);
        return null;
      }
    }
  }
  customElements.define("variant-picker", VariantPicker);
}